import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { FC, Fragment, JSX, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import FormCard from './FormCard';
import { Alert } from 'components/common/Alert';
import DataLoader from 'components/common/DataLoader';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import { baseCoatingSchema } from 'validationSchema';
import { ParamType, BaseCoatingFormType } from 'interfaces';
import { flexCenterBetween } from 'styles/commonComponentStyle';
import {
  useCreateBaseCoatingMutation,
  useUpdateBaseCoatingMutation,
  useGetBaseCoatingLazyQuery,
  TabletCoatingPayload,
  CreateTabletCoatingInput,
} from 'generated/graphql';
import { ADD_TEXT, HTTP_STATUS, BASE_COATING_TEXT, baseCoatingInitialValue } from 'constants/index';
import { useAddTitleText, useUpdateTitleText, useAddLoadingText, useUpdateLoadingText } from '../../../hooks';

const Edit: FC = (): JSX.Element => {
  const addText = useAddTitleText(BASE_COATING_TEXT);
  const updateText = useUpdateTitleText(BASE_COATING_TEXT);
  const creatingText = useAddLoadingText(BASE_COATING_TEXT);
  const updatingText = useUpdateLoadingText(BASE_COATING_TEXT);

  const navigation = useNavigate();
  const params = useParams<ParamType>();
  const { id } = params || {};

  const methods = useForm<BaseCoatingFormType>({
    defaultValues: baseCoatingInitialValue,
    resolver: yupResolver(baseCoatingSchema),
  });

  const { handleSubmit, setValue } = methods;

  const [createBaseCoating, { loading: createLoading }] = useCreateBaseCoatingMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { createTabletCoating } = data;
      const { response } = createTabletCoating || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.CREATED) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [updateBaseCoating, { loading: updateLoading }] = useUpdateBaseCoatingMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateTabletCoating } = data;
      const { response } = updateTabletCoating || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [getBaseCoating, { loading: getLoading }] = useGetBaseCoatingLazyQuery({
    onCompleted: (data) => {
      const { getTabletCoating } = data || {};
      const { response, tabletCoating } = getTabletCoating || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setValues(tabletCoating as TabletCoatingPayload['tabletCoating']);
      }
    },
    onError: () => {
      setValues(null);
    },
  });

  const onSubmit: SubmitHandler<BaseCoatingFormType> = async (data) => {
    const { name, costPerKg, loadAdd, productType } = data || {};
    const { value: productTypeId } = productType || {};

    const input: CreateTabletCoatingInput = {
      name,
      productTypeId,
      costPerKg: Number(costPerKg),
      loadAdd: Number(loadAdd),
    };

    if (id) {
      await updateBaseCoating({
        variables: {
          updateTabletCoatingInput: {
            id: id || '',
            ...input,
          },
        },
      });
    } else {
      await createBaseCoating({
        variables: {
          createTabletCoatingInput: input,
        },
      });
    }
  };

  const fetchBaseCoating = useCallback(async () => {
    id &&
      (await getBaseCoating({
        variables: {
          getTabletCoatingInput: {
            id,
          },
        },
      }));
  }, [id, getBaseCoating]);

  const setValues = (params: TabletCoatingPayload['tabletCoating']) => {
    const { name, costPerKg, loadAdd, productType } = params || {};
    const { id: productTypeId, name: productTypeName } = productType || {};

    setValue('name', name || '');
    setValue('loadAdd', `${loadAdd || ''}`);
    setValue('costPerKg', `${costPerKg || ''}`);
    setValue('productType', { value: productTypeId || '', name: productTypeName || '' });
  };

  useEffect(() => {
    id && fetchBaseCoating();
  }, [id, fetchBaseCoating]);

  const loading = updateLoading || createLoading;

  return (
    <Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={flexCenterBetween}>
            <Typography variant="h5">{id ? updateText : addText}</Typography>
            {!id && (
              <Button variant="contained" type="submit">
                {ADD_TEXT}
              </Button>
            )}
          </Box>

          {getLoading ? <DataLoader columns={12} rows={6} /> : <FormCard loading={loading} isEdit={!!id} />}
        </form>
      </FormProvider>

      <BackdropLoader open={loading} text={id ? updatingText : creatingText} />
    </Fragment>
  );
};

export default Edit;
